/**
 * Product Metadata Utilities
 * 
 * Helpers for building product-related metadata for cart items.
 * Handles: product names, SKUs, weights, color formatting, size formatting
 */

/**
 * Format color name (capitalize words, handle dashes)
 * 
 * Examples:
 * - "white" → "White"
 * - "navy-blue" → "Navy Blue"
 * 
 * @param {string} colorName - Raw color name
 * @returns {string} Formatted color name
 */
export function formatColorName(colorName) {
    if (!colorName) return '';
    
    if (colorName.includes("-")) {
        return colorName.split("-")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    }
    
    return colorName.charAt(0).toUpperCase() + colorName.slice(1);
}

/**
 * Rename size code to full name
 * 
 * Examples:
 * - "XS" → "X-Small"
 * - "L" → "Large"
 * - "2XL" → "2X-Large"
 * 
 * @param {string} size - Size code
 * @returns {string} Full size name
 */
export function renameSize(size) {
    const sizeMap = {
        'XS': 'X-Small',
        'S': 'Small',
        'M': 'Medium',
        'L': 'Large',
        'XL': 'X-Large',
        '2XL': '2X-Large',
        '3XL': '3X-Large',
        '4XL': '4X-Large',
        '5XL': '5X-Large'
    };
    
    return sizeMap[size] || size;
}

/**
 * Build product name for cart item
 * 
 * Formats:
 * - Grouped plain: "Product Name - (Color)"
 * - Regular: "Product Name - Size (Color)"
 * 
 * @param {Object} colorSizeLine - Color line data from selectionStore
 * @param {string} size - Size code
 * @param {Object} appStore - App store instance
 * @param {Object} imprintStore - Imprint store instance
 * @returns {Object} Formatted product name
 */
export function buildProductName(colorSizeLine, size, appStore, imprintStore) {
    const productName = imprintStore.fullJson?.name || 'Product';
    const colorName = formatColorName(colorSizeLine.slectedColor.title);

    let names = {};

    // For grouped_plain products, no size in name
    if (appStore.productType === 'grouped_plain') {
        names.cart = `${colorName}`;
        names.order = `${productName} - (${colorName})`;
        return names;
    }

    const sizeName = renameSize(size);

    names.cart = `${colorName} - ${sizeName}`;
    names.order = `${productName} - ${sizeName} (${colorName})`;

    return names;
}

/**
 * Build product SKU for simple_array lookup
 *
 * Formats:
 * - Grouped plain: Uses img_sku directly
 * - Regular: "CATALOG_SKU_SIZE_CODE_SKU_COLOR_CODE" (e.g., "G640_L_0001", "CM1401_1S_0001")
 *
 * Always uses sku_size_code and sku_color_code from simple_array when available,
 * because the display "size" value may differ from the code used in SKU
 * (e.g., size="1-SIDE" but sku_size_code="1S" for CM1401).
 *
 * @param {Object} colorSizeLine - Color line data from selectionStore
 * @param {string} size - Size display value (e.g., "1-SIDE", "XS")
 * @param {Object} appStore - App store instance
 * @param {Object} imprintStore - Imprint store instance
 * @param {Object} selectionStore - Selection store instance
 * @returns {string} Product SKU
 */
export function buildProductSKU(colorSizeLine, size, appStore, imprintStore, selectionStore) {
    // For grouped_plain, use img_sku directly
    if (appStore.productType === "grouped_plain") {
        return colorSizeLine.slectedColor.img_sku;
    }

    const catalogCode = imprintStore.fullJson?.catalog_code || '';
    const sku          = imprintStore.selectedImprintMethod?.sku;
    const simpleArray  = imprintStore.fullJson?.configurable_array?.[sku]?.simple_array;
    const colorName    = colorSizeLine.slectedColor.title?.toLowerCase();

    // Lookup sku_size_code and sku_color_code from simple_array
    // All products share the same JSON structure, so this is the single source of truth
    if (simpleArray) {
        const entry = Object.values(simpleArray).find(
            e => e.color === colorName && e.size === size
        );

        if (entry?.sku_size_code && entry?.sku_color_code) {
            return `${catalogCode}_${entry.sku_size_code}_${entry.sku_color_code}`;
        }

        console.warn(
            `[ProductMetadata] buildProductSKU: no simple_array match for color="${colorName}" size="${size}" — using fallback`
        );
    }

    // Fallback: parse color code from img_sku (last segment) + use size as-is
    const colorSkuArr = colorSizeLine.slectedColor.img_sku?.split('_') || [];
    const colorCode   = colorSkuArr[colorSkuArr.length - 1] || '';
    return `${catalogCode}_${size}_${colorCode}`;
}

/**
 * Get product weight from simple_array
 * 
 * @param {Object} colorSizeLine - Color line data from selectionStore
 * @param {string} size - Size code
 * @param {Object} imprintStore - Imprint store instance
 * @param {Object} appStore - App store instance
 * @param {Object} selectionStore - Selection store instance
 * @returns {number} Product weight
 */
export function getProductWeight(colorSizeLine, size, imprintStore, appStore, selectionStore) {
    const sku = imprintStore.selectedImprintMethod.sku;
    const productSKU = buildProductSKU(colorSizeLine, size, appStore, imprintStore, selectionStore);
    
    const configurableData = imprintStore.fullJson?.configurable_array?.[sku];
    const simpleData = configurableData?.simple_array?.[productSKU];
    
    return simpleData?.weight || 0;
}

/**
 * Build item metadata for backend
 * 
 * Includes: price, productColorID, imgID, imgGID, color_sku, imprint_sku, mainProduct_id
 * 
 * @param {Object} colorSizeLine - Color line data from selectionStore
 * @param {string} size - Size code
 * @param {Object} appStore - App store instance
 * @param {Object} imprintStore - Imprint store instance
 * @param {Object} selectionStore - Selection store instance
 * @param {number} priceByQty - Price per item
 * @returns {Object} Item metadata
 */
export function buildItemMetaData(
    colorSizeLine, 
    size, 
    appStore, 
    imprintStore, 
    selectionStore,
    priceByQty
) {
    // Get color product ID
    let colorProductId = colorSizeLine.slectedColor.id;
    if (appStore.associatedIds && appStore.associatedIds.simples?.id_color) {
        colorProductId = appStore.associatedIds.simples.id_color;
    }
    
    return {
        price: priceByQty.toFixed(2),
        productColorID: colorProductId,
        imgID: colorSizeLine.slectedColor.img_id,
        imgGID: colorSizeLine.slectedColor.img_gid || null,
        color_sku: buildProductSKU(colorSizeLine, size, appStore, imprintStore, selectionStore),
        imprint_sku: imprintStore.selectedImprintMethod.sku,
        mainProduct_id: appStore.mainProductID
    };
}

/**
 * Get color product ID from associatedIds or fallback to variant ID
 * 
 * @param {Array} lines - Color lines from selectionStore
 * @param {Object} appStore - App store instance
 * @returns {number} Color product ID
 */
export function getColorProductId(lines, appStore) {
    let colorProductId = lines[0]?.slectedColor?.id;
    
    if (appStore.associatedIds && appStore.associatedIds.simples?.id_color) {
        colorProductId = appStore.associatedIds.simples.id_color;
        console.log('[ProductMetadata] Using colorProductId from associatedIds:', colorProductId);
    } else {
        console.warn('[ProductMetadata] WARNING: associatedIds not loaded, using variant ID as fallback:', colorProductId);
    }
    
    return colorProductId;
}
